import debounce from 'lodash/debounce';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  type ModelLeadClientActionHistoryFilters,
  useGetLeadClientActionHistoryFiltersQuery,
  useSetLeadClientActionHistoryFiltersMutation,
} from '@app/api/ActionsHistory';
import { WARNING } from '@app/constants';
import { useUserPrint } from '@app/hooks';
import { useNotification } from '@app/providers/NotificationProvider/NotificationContext';
import { useErrorHandler } from '@app/shared/hooks';

// Бэк перезаписывает настройки целиком, поэтому сохраняем после паузы в
// переключениях, а не на каждый клик (договорённость с бэком по us/11168).
const SAVE_DEBOUNCE_MS = 500;

// Снятые пользователем пункты «Интеграции» (SocialType) и «Действия»
// (ActionId). Хранятся только скрытые значения: новые значения, которых нет в
// списках, показываются включёнными.
export interface LeadHistoryHiddenFilters {
  actionIds: string[];
  socialTypes: string[];
}

type HiddenFiltersUpdater = (
  prev: LeadHistoryHiddenFilters
) => LeadHistoryHiddenFilters;

interface UseLeadHistoryFilterSettingsParams {
  // false — настройки живут только в памяти карточки, бэк не трогаем.
  isEnabled: boolean;
}

interface UseLeadHistoryFilterSettingsResult {
  hiddenFilters: LeadHistoryHiddenFilters;
  updateHiddenFilters(updater: HiddenFiltersUpdater): void;
}

const EMPTY_HIDDEN_FILTERS: LeadHistoryHiddenFilters = {
  actionIds: [],
  socialTypes: [],
};

const toHiddenFilters = ({
  HiddenActionIds,
  HiddenSocialTypes,
}: ModelLeadClientActionHistoryFilters): LeadHistoryHiddenFilters => ({
  actionIds: HiddenActionIds ?? [],
  socialTypes: HiddenSocialTypes ?? [],
});

// Настройки фильтров истории карточки лида хранятся на бэке и общие для всех
// лидов пользователя (us/11168): GET при открытии карточки, при изменении —
// сразу локально и отложенный POST с полными списками скрытых значений. Ответ
// POST — источник истины.
export const useLeadHistoryFilterSettings = ({
  isEnabled,
}: UseLeadHistoryFilterSettingsParams): UseLeadHistoryFilterSettingsResult => {
  const {
    params: { ActiveBranchId },
  } = useUserPrint();
  const { showNotification } = useNotification();

  const shouldSync = isEnabled && ActiveBranchId != null;

  const {
    data: savedFilters,
    isError,
    error,
  } = useGetLeadClientActionHistoryFiltersQuery(
    { ActiveBranchId },
    { skip: !shouldSync }
  );
  const [saveFilters] = useSetLeadClientActionHistoryFiltersMutation();

  useErrorHandler([{ flag: isError, error }]);

  const [hiddenFilters, setHiddenFilters] =
    useState<LeadHistoryHiddenFilters>(EMPTY_HIDDEN_FILTERS);
  const hiddenFiltersRef = useRef(hiddenFilters);

  // Номер последнего локального изменения и последнего, подтверждённого
  // бэком. Пока есть неподтверждённые изменения, данные с сервера (запоздавший
  // GET или ответ на более раннее сохранение) не перетирают новые клики.
  const localVersionRef = useRef(0);
  const savedVersionRef = useRef(0);

  const applyHiddenFilters = useCallback((next: LeadHistoryHiddenFilters) => {
    hiddenFiltersRef.current = next;
    setHiddenFilters(next);
  }, []);

  useEffect(() => {
    if (!savedFilters) return;
    if (localVersionRef.current !== savedVersionRef.current) return;

    applyHiddenFilters(toHiddenFilters(savedFilters));
  }, [applyHiddenFilters, savedFilters]);

  const saveHiddenFilters = useCallback(
    async (next: LeadHistoryHiddenFilters, version: number) => {
      try {
        const response = await saveFilters({
          ActiveBranchId,
          data: {
            HiddenActionIds: next.actionIds,
            HiddenSocialTypes: next.socialTypes,
          },
        }).unwrap();

        if (version !== localVersionRef.current) return;

        savedVersionRef.current = version;
        applyHiddenFilters(toHiddenFilters(response));
      } catch (saveError) {
        showNotification(saveError, WARNING);
      }
    },
    [ActiveBranchId, applyHiddenFilters, saveFilters, showNotification]
  );

  const saveHiddenFiltersDebounced = useMemo(
    () =>
      debounce((next: LeadHistoryHiddenFilters, version: number) => {
        void saveHiddenFilters(next, version);
      }, SAVE_DEBOUNCE_MS),
    [saveHiddenFilters]
  );

  // Карточку закрыли раньше, чем истекла пауза, — сохраняем сразу, иначе
  // последнее переключение потеряется.
  useEffect(
    () => () => {
      saveHiddenFiltersDebounced.flush();
    },
    [saveHiddenFiltersDebounced]
  );

  const updateHiddenFilters = useCallback(
    (updater: HiddenFiltersUpdater) => {
      const next = updater(hiddenFiltersRef.current);

      localVersionRef.current += 1;
      applyHiddenFilters(next);

      if (shouldSync) {
        saveHiddenFiltersDebounced(next, localVersionRef.current);
      } else {
        savedVersionRef.current = localVersionRef.current;
      }
    },
    [applyHiddenFilters, saveHiddenFiltersDebounced, shouldSync]
  );

  return useMemo(
    () => ({ hiddenFilters, updateHiddenFilters }),
    [hiddenFilters, updateHiddenFilters]
  );
};
